import React, {useEffect, useState} from 'react';
import {
    Box,
    Container,
    Tab,
    Tabs,
} from '@mui/material';

import {When} from "react-if";
import {useSelector} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {RiskAssessmentResultPage} from "../assessment-result";
import {RiskAssessmentSearch} from "../search";
import {RiskAssessmentHistory} from "../history";
import {AllSearches, AllSearchesView} from "../all-searches";
import {FlagResultDialog} from "../flag-dialog";
import dayjs, {Dayjs} from "dayjs";
import {IraSearchHistoryTypes, ReportTypes, RiskReportTypes} from "../../../../../types";
import {RootState} from "../../../../../store";
import {IraSearchHistoryService} from "../../../../../services/ira-search-history.service";
import {ReportService} from "../../../../../services/report.service";
import {styled} from "@mui/material/styles";
import PdfLetterHead from "../../../../../components/pdf-download-letterhead";
import {generatePDF} from "../../../../../helpers/downloadPdf";
import {toast} from "react-toastify";
import { useEffectiveSettings, SETTING } from "../../../../../hooks/use-effective-settings";
import {validateStreetAddress} from "../../../../../helpers/addressValidator";
import {ConfirmSearchDialog, ConfirmSearchField} from "../confirm-search-dialog";
import {parsePersonName} from "../../../../../utils/person-name";
import {canUseIraSearchHistory} from "../../../../../helpers/permissionCheck";
import {RESPONSE_CODES} from "../../../../../constants/responseCodes.constants";

const PdfDownloadDiv = styled('div')(({ theme }) => ({
    '.div-border-for-download': {border:'1px solid #ddd'},
    table: {borderCollapse:'collapse'},
    '.text-download-adjust, .MuiChip-label, .MuiChip-labelSmall':{
        'min-height' : '35px !important'
    },

}));

interface Props {setTitle: (title: string) => void;}
type StateOption = { name: string; abbreviation: string; };

const STATE_CAPITALS: Record<string, string> = {
    AL: 'Montgomery',
    AK: 'Juneau',
    AZ: 'Phoenix',
    AR: 'Little Rock',
    CA: 'Sacramento',
    CO: 'Denver',
    CT: 'Hartford',
    DE: 'Dover',
    FL: 'Tallahassee',
    GA: 'Atlanta',
    HI: 'Honolulu',
    ID: 'Boise',
    IL: 'Springfield',
    IN: 'Indianapolis',
    IA: 'Des Moines',
    KS: 'Topeka',
    KY: 'Frankfort',
    LA: 'Baton Rouge',
    ME: 'Augusta',
    MD: 'Annapolis',
    MA: 'Boston',
    MI: 'Lansing',
    MN: 'Saint Paul',
    MS: 'Jackson',
    MO: 'Jefferson City',
    MT: 'Helena',
    NE: 'Lincoln',
    NV: 'Carson City',
    NH: 'Concord',
    NJ: 'Trenton',
    NM: 'Santa Fe',
    NY: 'Albany',
    NC: 'Raleigh',
    ND: 'Bismarck',
    OH: 'Columbus',
    OK: 'Oklahoma City',
    OR: 'Salem',
    PA: 'Harrisburg',
    RI: 'Providence',
    SC: 'Columbia',
    SD: 'Pierre',
    TN: 'Nashville',
    TX: 'Austin',
    UT: 'Salt Lake City',
    VT: 'Montpelier',
    VA: 'Richmond',
    WA: 'Olympia',
    WV: 'Charleston',
    WI: 'Madison',
    WY: 'Cheyenne',
    DC: 'Washington'
};

const RiskAssessmentSearchAndHistory: React.FC<Props> = ({setTitle}) => {
    const { getSetting, isLoading: settingsLoading } = useEffectiveSettings();
    const onlyAddress = getSetting(SETTING.showRiskAssessmentAddressOnly, true);
    // Held false while settings load so orgs that have statewide disabled
    // never see the toggle flash in before their settings resolve.
    const showStatewideSearch = !settingsLoading && getSetting(SETTING.showStatewideSearch, true);
    const showApiDocsLink = !settingsLoading && getSetting<string>(SETTING.organizationType, 'full') === 'iraStandalone';
    // All Searches tab + Flag for support (868mkwmk5), POM-set per org.
    const { authUser } = useSelector((state: RootState) => state.auth);
    // Same rule as the API, so nobody gets a tab that only answers 403.
    const showSearchHistory = !settingsLoading && getSetting<boolean>(SETTING.iraSearchHistory, false)
        && canUseIraSearchHistory(authUser);
    const isSupport = !!(authUser?.masquarade || authUser?.pomMaster);
    // The support email links to ?view=flagged&ref=IRA-XXXXXXXX (that one report);
    // ?view=all and ?view=flagged open the tab too.
    const [searchParams, setSearchParams] = useSearchParams();
    const viewParam = searchParams.get('view');
    const refParam = searchParams.get('ref');
    const [historyTab, setHistoryTab] = useState<'saved' | 'all'>(viewParam === 'all' || viewParam === 'flagged' || !!refParam ? 'all' : 'saved');
    const [allSearchesView, setAllSearchesView] = useState<AllSearchesView>(viewParam === 'flagged' || !!refParam ? 'flagged' : 'all');
    const [pastSearch, setPastSearch] = useState<IraSearchHistoryTypes.SearchDetail | null>(null);
    const [rowFlagTarget, setRowFlagTarget] = useState<IraSearchHistoryTypes.FlagTarget | null>(null);
    const [allSearchesRefresh, setAllSearchesRefresh] = useState(0);
    const [currentView, setCurrentView] = useState<'search' | 'details'>('search');
    const [dob, setDob] = React.useState<Dayjs | null>(null);
    const [address, setAddress] = React.useState<RiskReportTypes.Address>({address:'', placeId:''});
    const [riskReportData,setRiskReportData] = useState<RiskReportTypes.RiskResponse | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [patientName, setPatientName] = useState<string>('');
    const [age, setAge] = useState<string>('');
    const [selectedReport, setSelectedReport] = useState<ReportTypes.Model | null>(null);

    const [isAddressError, setIsAddressError] = useState<boolean>(false);
    const [selectedState, setSelectedState] = useState<StateOption | null>(null);
    const [isStatewide, setIsStatewide] = useState<boolean>(false);
    const [confirmSearchOpen, setConfirmSearchOpen] = useState<boolean>(false);

    // Review step before the search runs: mistyped names produce wrong,
    // paid-for reports, so the user confirms their input first. Validation
    // here mirrors the guards at the top of runSearch.
    const handleSearch = () => {
        if (isStatewide) {
            if (!selectedState) {
                setIsAddressError(true);
                return;
            }
        } else {
            const hasAddress = !!address.address;
            if (hasAddress && !validateStreetAddress(address.address)) {
                setIsAddressError(true);
                setAddress({address: '', placeId: ''});
                return;
            }
            if (!hasAddress && !selectedState) {
                setIsAddressError(true);
                return;
            }
        }
        setIsAddressError(false);
        setConfirmSearchOpen(true);
    };

    const confirmSearchFields = (): ConfirmSearchField[] => {
        const fields: ConfirmSearchField[] = [];
        if (!onlyAddress) {
            fields.push({ label: 'Name', value: patientName.trim() || 'Not provided' });
            fields.push({ label: 'Date of birth', value: dob ? dob.format('MM/DD/YYYY') : 'Not provided' });
            if (age) fields.push({ label: 'Age', value: age });
        }
        if (isStatewide) {
            fields.push({ label: 'Search area', value: `${selectedState?.name ?? ''} (statewide)`, fullWidth: true });
        } else {
            fields.push({ label: 'Address', value: address.address || `${selectedState?.name ?? ''} (state capital)`, fullWidth: true });
        }
        return fields;
    };

    const openPastSearch = async (searchId: string) => {
        try {
            const detail = await IraSearchHistoryService.getSearch(searchId);
            if (!detail?.riskAssessmentResult) {
                toast(detail?.unreadable
                    ? "This search's details could not be decrypted, so it can't be opened"
                    : 'This search result is no longer available', { type: 'error', autoClose: 3000 });
                return;
            }
            setSelectedReport(null);
            setPastSearch(detail);
            setCurrentView('details');
        } catch (e: any) {
            console.error('[RiskAssessment] open past search failed:', e);
            const code: string | undefined = e?.response?.data?.message;
            toast((code && RESPONSE_CODES[code]) || 'Could not open this search', { type: 'error', autoClose: 3000 });
        }
    };

    const changeHistoryTab = (tab: 'saved' | 'all') => {
        setHistoryTab(tab);
        const next = new URLSearchParams(searchParams);
        if (tab === 'all') {
            next.set('view', allSearchesView);
        } else {
            // Leaving the tab drops its reference filter too, so coming back
            // (or a refresh) can't reapply a filter the user already left.
            next.delete('view');
            next.delete('ref');
        }
        setSearchParams(next, { replace: true });
    };

    const changeAllSearchesView = (view: AllSearchesView) => {
        setAllSearchesView(view);
        const next = new URLSearchParams(searchParams);
        next.set('view', view);
        if (view !== 'flagged') next.delete('ref');
        setSearchParams(next, { replace: true });
    };

    const changeReference = (reference: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (reference) next.set('ref', reference); else next.delete('ref');
        setSearchParams(next, { replace: true });
    };

    const runSearch = async () => {
        const {firstName, middleName, lastName} = parsePersonName(patientName);
        setSelectedReport(null)
        setPastSearch(null)
        const hasAddress = !!address.address;
        const hasState = !!selectedState;

        const geocodeStateCapital = async (stateOption: StateOption): Promise<{ formattedAddress: string; placeId: string }> => {
            const capital = STATE_CAPITALS[stateOption.abbreviation] || stateOption.name;
            const query = `${capital}, ${stateOption.abbreviation}`;

            if (!window.google?.maps?.Geocoder) {
                return { formattedAddress: query, placeId: '' };
            }
            return new Promise((resolve) => {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode(
                    { address: query, componentRestrictions: { country: 'US', administrativeArea: stateOption.abbreviation } },
                    (results, status) => {
                        if (status === 'OK' && results && results.length > 0) {
                            resolve({
                                formattedAddress: results[0].formatted_address || query,
                                placeId: results[0].place_id || ''
                            });
                        } else {
                            resolve({ formattedAddress: query, placeId: '' });
                        }
                    }
                );
            });
        };

        if (isStatewide) {
            if (!hasState) {
                setIsAddressError(true);
                return;
            }
            setIsAddressError(false);
            const capitalResult = await geocodeStateCapital(selectedState!);
            setAddress({ address: capitalResult.formattedAddress, placeId: capitalResult.placeId });
            const apiAddressForGenerate = selectedState?.abbreviation || '';
            const requestObject: RiskReportTypes.RiskAssessmentRequest = {
                firstName : onlyAddress ? '' : firstName,
                middleName : onlyAddress ? '' : middleName,
                lastName : onlyAddress ? '' : lastName,
                dateOfBirth: onlyAddress ? '' :  dob ? dob.format('YYYY-MM-DD') : '',
                age : onlyAddress ? '' : age,
                address: apiAddressForGenerate
            };
            await ReportService.generateRiskReport(requestObject, setIsLoading, setRiskReportData, setCurrentView, ()=>{toast("Error generating report", {
                autoClose: 1500,
                type: 'error',
            });});
            return;
        }

        if(hasAddress && !validateStreetAddress(address.address)){
            setIsAddressError(true);
            setAddress({address:'', placeId:''});
            return;
        }

        let addressToUse: RiskReportTypes.Address = {...address};

        if(!hasAddress && hasState && selectedState){
            const capitalResult = await geocodeStateCapital(selectedState);
            addressToUse = {
                address: capitalResult.formattedAddress,
                placeId: capitalResult.placeId
            };
            setAddress(addressToUse);
        }

        if(!addressToUse.address){
            setIsAddressError(true);
            return;
        }

        setIsAddressError(false);

        const apiAddressForGenerate = hasAddress ? addressToUse.address : (selectedState?.abbreviation || addressToUse.address);

        const requestObject: RiskReportTypes.RiskAssessmentRequest = {
            firstName : onlyAddress ? '' : firstName,
            middleName : onlyAddress ? '' : middleName,
            lastName : onlyAddress ? '' : lastName,
            dateOfBirth: onlyAddress ? '' :  dob ? dob.format('YYYY-MM-DD') : '',
            age : onlyAddress ? '' : age,
            address: apiAddressForGenerate
        };
        await ReportService.generateRiskReport(requestObject, setIsLoading, setRiskReportData, setCurrentView, ()=>{toast("Error generating report", {
            autoClose: 1500,
            type: 'error',
        });});
    }

    useEffect(() => {
        if(currentView === 'details')
            setTitle('Intake Risk Assessment')
        else
            // The search view's hero band carries the page header itself; an
            // empty title tells the page shell to skip its plain heading.
            setTitle('');
    }, [currentView]);

    // Same export UX as the Alerts list: announce the download and swallow
    // re-clicks while the multi-second canvas render runs.
    const isExportingPdf = React.useRef<boolean>(false);
    const handleDownload = async (report:RiskReportTypes.RiskResponse) => {
        if (isExportingPdf.current) return;
        isExportingPdf.current = true;
        toast('Download starting...', { type: 'info', autoClose: 2000 });

        // The `?.` was on `report` only, so a report with no riskAssessmentInfo
        // threw here. Export is fire-and-forget, so that surfaced as an unhandled
        // rejection and a button that silently did nothing. Those rows are newly
        // clickable now that the history table renders sparse reports.
        let fileName = report?.riskAssessmentInfo?.searchedPerson?.address?.replaceAll(" ","_")+'__'+dayjs(new Date()).format('MM_DD_YYYY__hh:mm_A')

        const componentJsx =  <PdfLetterHead>
            <PdfDownloadDiv>
                {report &&
                    (
                        <RiskAssessmentResultPage
                            isView={true}
                            address={address}
                            assessmentData={report}/>
                    )
                }

            </PdfDownloadDiv>
        </PdfLetterHead>
        const {downloadPDF} = generatePDF();

        try {
            await downloadPDF(componentJsx, {
                fileName,
                margins: 1,
                scale: 3,
                stretch : 1,
                containerWidth : '1600px',
                orientation: 'portrait'
            });
        } catch (e) {
            console.error('[handleDownload] PDF export failed:', e);
            toast('Export failed. Please try again.', { type: 'error' });
        } finally {
            isExportingPdf.current = false;
        }
    }

    return (
        <>
            <When condition={currentView === 'search'}>
                <Container maxWidth={false}>
                    {/* Generate New Risk Assessment Form */}
                    <RiskAssessmentSearch
                        patientName={patientName} setPatientName={setPatientName}
                        dob={dob} setDob={setDob}
                        age={age} setAge={setAge}
                        address={address} setAddress={setAddress}
                        loading={isLoading}
                        handleSearch={handleSearch}
                        onlyAddress={onlyAddress}
                        showStatewideSearch={showStatewideSearch}
                        showApiDocsLink={showApiDocsLink}
                        isAddressError={isAddressError}
                        setIsAddressError={setIsAddressError}
                        selectedState={selectedState}
                        setSelectedState={setSelectedState}
                        isStatewide={isStatewide}
                        setIsStatewide={setIsStatewide}
                    />

                    {showSearchHistory && (
                        <Box sx={{ maxWidth: 1240, mx: 'auto', mb: 2 }}>
                            <Tabs value={historyTab} onChange={(_, tab) => changeHistoryTab(tab)}>
                                <Tab value="saved" label="Saved reports" />
                                <Tab value="all" label="All searches" />
                            </Tabs>
                        </Box>
                    )}

                    {showSearchHistory && historyTab === 'all' ? (
                        <AllSearches
                            initialView={allSearchesView}
                            onViewChange={changeAllSearchesView}
                            initialReference={searchParams.get('ref')}
                            onReferenceChange={changeReference}
                            isSupport={isSupport}
                            canFlag={!authUser?.masquarade}
                            onOpenSearch={openPastSearch}
                            onFlag={setRowFlagTarget}
                            refreshKey={allSearchesRefresh}
                        />
                    ) : (
                        /* Past Risk Assessment Reports Table */
                        <RiskAssessmentHistory
                            selectedReport={selectedReport}
                            handleDownload={handleDownload}
                            setSelectedReport={(report) => { setPastSearch(null); setSelectedReport(report); }}
                            setCurrentView={setCurrentView}/>
                    )}
                </Container>
            </When>
            <When condition={currentView ==='details'}>
                <RiskAssessmentResultPage
                    // A past search that was saved is shown like the saved report: no Save button.
                    isView={!!selectedReport || !!pastSearch?.savedReportId}
                    isPastSearch={!!pastSearch}
                    flags={pastSearch?.flags}
                    handleDownload={handleDownload}
                    setCurrentView={setCurrentView}
                    report={selectedReport}
                    address={selectedReport
                        ? {address : selectedReport.place?.address!, placeId : selectedReport.place?.placeId!}
                        : pastSearch ? {address: pastSearch.address ?? '', placeId: pastSearch.placeId ?? ''} : address}
                    assessmentData={selectedReport?.riskAssessmentResult || pastSearch?.riskAssessmentResult || riskReportData!}/>
            </When>

            <FlagResultDialog
                open={!!rowFlagTarget}
                target={rowFlagTarget}
                onClose={() => setRowFlagTarget(null)}
                onFlagged={() => setAllSearchesRefresh((n) => n + 1)}
            />

            <ConfirmSearchDialog
                open={confirmSearchOpen}
                fields={confirmSearchFields()}
                onCancel={() => setConfirmSearchOpen(false)}
                onConfirm={() => {
                    setConfirmSearchOpen(false);
                    runSearch();
                }}
            />
        </>
    );
};

export default RiskAssessmentSearchAndHistory;